import React, { useEffect, useRef } from 'react';
import { createTheme, Grid, ThemeProvider } from '@mui/material';
import Routes from './routes';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { useDispatch, useSelector } from 'react-redux';
import {
  Authenticated,
  defaultLanguage,
  fetchUserContext,
  getTheme,
  organizationInfo,
  parentOrgInitData,
} from './utils/redux/reducer/authentication-slice';
import 'dayjs/locale/pt';
import 'dayjs/locale/en';
import Config from 'utils/Config';
import Clarity from '@microsoft/clarity';
import { fetchFromStorage } from 'utils/storage';
import { identifiers } from 'utils/constants/identifiers';
import { applyNativeStorageSnapshot, notifyWebViewReady, onNativeMessage } from 'utils/webviewBridge';

function App() {
  const dispatch = useDispatch();
  const { faviconUrl, title, msClarityId } = useSelector(parentOrgInitData);
  const isLoggedIn = useSelector(Authenticated);
  const clarityInitialized = useRef(false);
  const userContextHydrated = useRef(false);

  // Re-hydrate the user context (privileges, integration, feature flags, …) on every
  // browser refresh. We only do this when there's an access token in storage, so the
  // login screen doesn't fire an unauthenticated call. Guarded by a ref so React 18
  // strict-mode double mounts don't make the request twice.
  useEffect(() => {
    if (userContextHydrated.current) return;
    const accessToken = fetchFromStorage(identifiers.access_token);
    if (!accessToken) return;
    userContextHydrated.current = true;
    dispatch(fetchUserContext() as any);
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    notifyWebViewReady();

    return onNativeMessage((message) => {
      if (message?.type === 'RN_STORAGE_HYDRATE') {
        applyNativeStorageSnapshot(message.storage);
      }
    });
  }, []);

  const tempTheme = {
    primary: '#ff6905',
    primaryTextColor: '#ffffff',
    secondary: '#ffffff',
    secondaryTextColor: '#141010',
    logoBackgroundColor: '#000000',
  };
  const orgInfo = useSelector(organizationInfo) as any;
  const a = orgInfo?.selectedOrganization ? orgInfo.selectedOrganization?.label : title;
  document.title = isLoggedIn ? a : title;

  useEffect(() => {
    const favicon = document.getElementById('favicon') as HTMLLinkElement;
    if (faviconUrl && favicon) {
      favicon.href = Config.BaseURL + faviconUrl;
    }
  }, [faviconUrl]);

  useEffect(() => {
    if (msClarityId && !clarityInitialized.current) {
      try {
        Clarity.init(msClarityId);
        clarityInitialized.current = true;
      } catch (error) {
        console.error('Failed to initialize Clarity:', error);
      }
    }
  }, [msClarityId]);

  const themesData = useSelector(getTheme);
  const themes = themesData?.primary ? themesData : tempTheme;
  const selectedlangLocaleCode = useSelector(defaultLanguage).value.split('_')[0];
  const overlayZIndex = 1400;
  const theme = createTheme({
    // direction: 'rtl',
    palette: {
      mode: 'light',
      primary: {
        main: themes.primary,
        contrastText: themes.primaryTextColor,
        light: themes.logoBackgroundColor,
      },
      secondary: {
        main: themes.secondary,
        contrastText: themes.secondaryTextColor,
      },
    },
    zIndex: {
      modal: 1300,
      snackbar: 1500,
      tooltip: 1600,
    },
    typography: {
      fontFamily: ['Inter'].join(','),
      fontSize: 13,
      h4: {
        fontSize: '1.375rem', // 22px
        fontWeight: 500,
        lineHeight: 1.3,
      },
      h5: {
        fontSize: '1.25rem', // 20px — aligns with pageTitleSx
        fontWeight: 500,
        lineHeight: 1.35,
      },
    },
    components: {
      MuiTextField: {
        defaultProps: {
          size: 'small',
          margin: 'none',
        },
      },
      MuiFormControl: {
        defaultProps: {
          size: 'small',
          margin: 'none',
        },
      },
      MuiSelect: {
        defaultProps: {
          size: 'small',
          MenuProps: {
            sx: { zIndex: overlayZIndex },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          root: {
            zIndex: overlayZIndex,
          },
        },
      },
      MuiMenu: {
        defaultProps: {
          sx: { zIndex: overlayZIndex },
        },
      },
      MuiDialog: {
        defaultProps: {
          sx: { zIndex: overlayZIndex },
        },
      },
      MuiAutocomplete: {
        defaultProps: {
          size: 'small',
          componentsProps: {
            popper: {
              sx: { zIndex: overlayZIndex },
            },
          },
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              paddingTop: 4,
              paddingBottom: 4,
            },
          },
          option: {
            fontSize: '0.875rem',
            minHeight: 36,
            paddingTop: 6,
            paddingBottom: 6,
          },
          inputRoot: {
            fontSize: '0.875rem',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
          },
        },
      },
      MuiOutlinedInput: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          root: {
            borderRadius: 6,
          },
          input: {
            fontSize: '0.875rem',
          },
          inputSizeSmall: {
            paddingTop: '8.5px',
            paddingBottom: '8.5px',
            paddingLeft: '12px',
            paddingRight: '12px',
          },
          multiline: {
            padding: 0,
          },
        },
      },
      MuiInputLabel: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            // Keep MUI's default shrink transform/scale so the outlined
            // notch width matches the rendered label (avoids border clipping).
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: '0.75rem',
            marginTop: 3,
            marginLeft: 2,
            lineHeight: 1.35,
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            marginLeft: -4,
            marginRight: 10,
          },
          label: {
            fontSize: '0.875rem',
          },
        },
      },
      MuiCheckbox: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiRadio: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            minHeight: 36,
            paddingTop: 7,
            paddingBottom: 7,
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={selectedlangLocaleCode}>
        <Grid container className="main-body">
          <Routes />
        </Grid>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
